import { useCallback, useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { manualSpinStart } from "../State/Slices/spinSlice";
import { incrementRoundIndex } from "../State/Slices/gameSettingsSlice";
import { Settings as Setting } from "../settings";
import { setFreeSpinsComplete } from "../State/Slices/freeSpinsSlice";
import { resetInfoAnimation } from "../State/Slices/animationsSlice";
const useFreeSpinReelsController = () => {
  const freeSpinReelRefs = useRef([]);
  const dispatch = useDispatch();
  const { linesRotationFinished, isSpinning, spinComplete } = useSelector(
    (state) => state.spin
  );
  const { freeSpinActive, freeSpinsComplete, freeSpinsLeft, isFreeSpinMode } =
    useSelector((state) => state.freeSpins);
  const { currentRound } = useSelector((state) => state.game);
  const { freeSpinInfoAnimationStarted, freeSpinInfoAnimationCompleted } =
    useSelector((state) => state.animations); // ✅ New state to control pause

  const timeoutsRef = useRef([]);
  const [awaitingNextSpin, setAwaitingNextSpin] = useState(false);
  const [timeoutActive, setTimeoutActive] = useState(false);

  const clearAllTimeouts = useCallback(() => {
    if (!isFreeSpinMode) {
      console.log("🛑 Skipping clearAllTimeouts: Not in free spin mode.");
      return;
    }

    console.log("🧹 Clearing all timeouts...");
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, [isFreeSpinMode]);

  const handleFreeSpin = useCallback(() => {
    if (!isFreeSpinMode) {
      console.log("🚫 Skipping handleFreeSpin: Not in free spin mode.");
      return;
    }

    if (freeSpinsComplete || freeSpinsLeft <= 0) {
      console.log("🚫 Free spins finished. No new spin should start.");
      return;
    }

    if (timeoutActive) {
      console.log("⏳ Timeout is still active, preventing early spin trigger.");
      return;
    }

    if (freeSpinInfoAnimationStarted) {
      console.log("⏸️ Free Spin paused due to Info Animation...");
      return;
    }

    if (freeSpinInfoAnimationCompleted) {
      dispatch(resetInfoAnimation());
    }

    console.log("✅ Starting Free Spin...");
    dispatch(incrementRoundIndex());
    dispatch(manualSpinStart());
    clearAllTimeouts();

    freeSpinReelRefs.current.forEach((reel) => {
      console.log(`🎰 Starting reel: ${reel?.reelIndex}`);
      reel?.startFreeSpin();
    });

    setAwaitingNextSpin(false);
  }, [
    dispatch,
    clearAllTimeouts,
    timeoutActive,
    freeSpinsComplete,
    freeSpinsLeft,
    isFreeSpinMode,
    freeSpinInfoAnimationCompleted,
    freeSpinInfoAnimationStarted, // ✅ Prevents spins while animation is running
  ]);

  const stopReels = useCallback(
    (delay) => {
      if (!isFreeSpinMode) {
        console.log("🚫 Skipping stopReels: Not in free spin mode.");
        return;
      }

      console.log("⏹️ Stopping reels with delay:", delay);
      clearAllTimeouts();

      freeSpinReelRefs.current.forEach((reel, index) => {
        const timeoutId = setTimeout(() => {
          console.log(`🛑 Stopping reel ${reel?.reelIndex}`);
          reel?.stopFreeSpin();
        }, delay * index);
        timeoutsRef.current.push(timeoutId);
      });
    },
    [clearAllTimeouts, isFreeSpinMode]
  );

  useEffect(() => {
    if (!isFreeSpinMode) {
      console.log("⛔ Skipping effect: Not in free spin mode.");
      return;
    }

    if (!freeSpinActive) {
      console.log("⛔ Free spin is not active");
      return;
    }

    if (freeSpinsComplete) {
      console.log("✅ Free spins are complete, blocking extra spin");
      clearAllTimeouts();
      return;
    }

    if (freeSpinsLeft === 0) {
      console.log("🏁 No free spins left, setting free spins complete");
      dispatch(setFreeSpinsComplete(true));
      return;
    }

    if (freeSpinInfoAnimationStarted) {
      console.log("⏸️ Free Spins paused due to Free Spin Info Animation.");
      return; // ✅ Pause free spins if animation is active
    }

    console.log("🎰 Free spin is active");
    console.log(`🔢 Free spins left: ${freeSpinsLeft}`);
    console.log(`🎲 Current round: ${JSON.stringify(currentRound)}`);
    console.log(`✅ Spin complete: ${spinComplete}`);
    console.log(`🕒 Awaiting next spin: ${awaitingNextSpin}`);

    if (spinComplete && !awaitingNextSpin) {
      console.log("⏳ Setting awaitingNextSpin to true...");
      setAwaitingNextSpin(true);

      if (!currentRound?.win) {
        console.log("💤 No win detected, delaying next spin...");
        setTimeoutActive(true);

        const timeoutId = setTimeout(() => {
          if (!freeSpinsComplete) {
            console.log("▶️ Triggering handleFreeSpin due to no win");
            setTimeoutActive(false);
            handleFreeSpin();
          } else {
            console.log("❌ Blocked handleFreeSpin after free spins complete.");
          }
        }, 600);
        timeoutsRef.current.push(timeoutId);
      }
    }
  }, [
    freeSpinActive,
    freeSpinsLeft,
    handleFreeSpin,
    freeSpinsComplete,
    currentRound,
    dispatch,
    spinComplete,
    awaitingNextSpin,
    isFreeSpinMode,
    clearAllTimeouts,
    freeSpinInfoAnimationStarted, // ✅ Prevents spin while animation is playing
  ]);

  useEffect(() => {
    if (!isFreeSpinMode) {
      console.log("⛔ Skipping effect: Not in free spin mode.");
      return;
    }

    if (freeSpinsComplete || freeSpinsLeft === 0) {
      console.log("🚫 Blocking spin since free spins are complete.");
      return;
    }

    if (freeSpinInfoAnimationStarted) {
      console.log("⏸️ Free Spins paused due to Free Spin Info Animation.");
      return; // ✅ Pause free spins if animation is active
    }

    if (
      spinComplete &&
      awaitingNextSpin &&
      linesRotationFinished &&
      !timeoutActive
    ) {
      console.log("🔄 Starting next free spin...");
      handleFreeSpin();
      setAwaitingNextSpin(false);
    }
  }, [
    linesRotationFinished,
    spinComplete,
    awaitingNextSpin,
    handleFreeSpin,
    timeoutActive,
    freeSpinsComplete,
    freeSpinsLeft,
    isFreeSpinMode,
    freeSpinInfoAnimationStarted, // ✅ Prevents spin while animation is playing
  ]);

  useEffect(() => {
    if (!isFreeSpinMode) {
      console.log("⛔ Skipping effect: Not in free spin mode.");
      return;
    }

    let timeoutId;

    if (isSpinning && !spinComplete) {
      console.log("⏳ Free spin in progress... Scheduling stop...");
      timeoutId = setTimeout(() => {
        console.log("🚨 Auto-stopping reels...");
        stopReels(Setting.STOP_DELAY_AUTO);
      }, Setting.AUTO_STOP_TIMEOUT);
      timeoutsRef.current.push(timeoutId);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isSpinning, spinComplete, stopReels, isFreeSpinMode]);

  return {
    freeSpinReelRefs,
    handleFreeSpin,
    handleFreeSpinStop: stopReels,
  };
};

export default useFreeSpinReelsController;
